import db from '../index';
import { PromiseExtended } from 'dexie';
import { InspectionInDto } from 'shared/dtos/in/inspection';
import { clear as inspectionsServiceClear } from './inspectionsService';
import { makeClearPublisher } from 'serviceWorker/broadcasts/publishers';
import { inspectionContract } from 'shared/domain/inspection/inspectionContract';
import { entityDbClearFactory } from '../entityDbClearFactory';
import {
  logWithRethrow,
  getErrorMsg,
  Operation,
  getErrorTransactionMsg,
} from '../helpers';
import { wrapQuery } from '../queryWrapper';
import { ProtocolItemInDto } from 'shared/dtos/in/protocolItem';
import { ProtocolSectionInDto } from 'shared/dtos/in/protocolSection';
import { DocumentInDto } from 'shared/dtos/in/document';
import { inDtoToDocumentInsertEntity } from 'serviceWorker/repository/document/documentInDtoToEntity';
import {
  upsertBatch as upsertDocumentsBatch,
  removeBatchByParentId as removeDocumentsBatch,
  removeSettledBatchByParentType as removeSettledDocumentsBatchByType,
} from '../documents';
import { InspectionEntity } from 'serviceWorker/repository/inspection/entity';
import { inspectionDtoToEntityToSave } from 'serviceWorker/repository/inspection/inspectionDtoToEntity';
import { normalizeProtocolItemInDto } from 'shared/domain/inspection/mapping/normalizeProtocolItemInDto';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { ProtocolAnswerInDto } from 'shared/dtos/in/protocolAnswer';
import { protocolAnswerInDtoToProtocolAnswer } from 'shared/domain/protocolAnswer/protocolAnswerInDtoToProtocolAnswer';
import { isComplianceProtocolAnswer } from 'shared/domain/protocolAnswer';
import { compareObjectIds } from 'shared/utils/id/compare';
import {
  makeDefaultCount,
  makeDefaultGetMany,
  makeDefaultGetOne,
} from '../defaultDaoFactories';
import { LogLevel } from 'shared/types/logger';

const clearInspections = wrapQuery(
  db,
  (): Promise<void> =>
    db.transaction(
      'rw',
      db.inspections,
      db.documents,
      async function transactionClearInspections(): Promise<void> {
        await Promise.all([
          db.inspections.clear().catch((e) =>
            logWithRethrow({
              logLevel: LogLevel.INFO,
              msg: getErrorMsg(Operation.clear, 'inspections'),
              errorObj: e,
              additionalInfo: null,
            })
          ),
          removeSettledDocumentsBatchByType('inspectionId'),
        ]);
      }
    )
);

export const clear = entityDbClearFactory(
  db,
  ['inspections', 'inspectionsService', 'documents'],
  clearInspections,
  inspectionsServiceClear,
  makeClearPublisher(inspectionContract)
);

export const quantity = makeDefaultCount<InspectionEntity>(db, db.inspections);

export const get = makeDefaultGetMany<InspectionEntity>(db, db.inspections);

export const getPublished = wrapQuery(
  db,
  async (): Promise<InspectionEntity[]> => {
    return db.inspections.where('deleted').equals(0).toArray();
  }
);

export const getOne = makeDefaultGetOne<string, InspectionEntity>(
  db,
  db.inspections,
  '_id'
);

export const updateDeleted = wrapQuery(
  db,
  async (inspectionId: string, deleted: 0 | 1): Promise<void> => {
    await db.inspections
      .update(inspectionId, { deleted })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'inspections'),
          errorObj: e,
          additionalInfo: { query: { inspectionId, deleted } },
        })
      );
  }
);

export const addBatch = wrapQuery(
  db,
  (data: InspectionInDto[]): PromiseExtended<unknown> => {
    return db
      .transaction('rw', db.inspections, db.documents, async function () {
        const inspectionIds = data.map((inspection) => inspection._id);
        const existingInspections = await db.inspections
          .where('_id')
          .anyOf(inspectionIds)
          .toArray();
        const existingSectionsByInspectionId = new Map(
          existingInspections.map((inspection) => [
            inspection._id,
            inspection.sections || [],
          ])
        );

        const inspectionsToSave = data.map((inspection) => {
          const hasSectionsInPayload = Array.isArray(
            (inspection as InspectionInDto & { sections?: unknown }).sections
          );

          const mappedInspection = inspectionDtoToEntityToSave(inspection);
          if (hasSectionsInPayload) {
            return mappedInspection;
          }

          return {
            ...mappedInspection,
            sections:
              existingSectionsByInspectionId.get(inspection._id) || [],
          };
        });

        const documentsData = data.flatMap(
          (inspection: InspectionInDto) => {
            const protocolDocs = inspection.protocol.flatMap(
              (protocolItem: ProtocolItemInDto) => {
                const documents =
                  protocolItem.documents ??
                  protocolItem.complianceCheck?.documents ??
                  [];
                return documents.map((document: DocumentInDto) =>
                  inDtoToDocumentInsertEntity(document, {
                    inspectionId: inspection._id,
                    protocolId: protocolItem._id,
                  })
                );
              }
            );

            const sectionDocs = (inspection.sections ?? []).flatMap(
              (section: ProtocolSectionInDto) => {
                const documents = section.documents ?? [];
                return documents.map((document: DocumentInDto) =>
                  inDtoToDocumentInsertEntity(document, {
                    inspectionId: inspection._id,
                    sectionId: section._id,
                  })
                );
              }
            );

            return [...protocolDocs, ...sectionDocs];
          }
        );

        return Promise.all([
          db.inspections.bulkPut(inspectionsToSave),
          upsertDocumentsBatch(documentsData),
        ]);
      })
      .catch((e) => {
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorTransactionMsg(
            [Operation.addBatch],
            ['inspections', 'documents']
          ),
          errorObj: e,
          additionalInfo: {},
        });
      });
  }
);

export const updateBatch = addBatch;

export const upsertProtocolAnswer = wrapQuery(
  db,
  async ({
    inspectionId,
    protocolItemId,
    answer,
  }: {
    inspectionId: string;
    protocolItemId: string;
    answer: ProtocolAnswerInDto;
  }): Promise<void> => {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) {
      return;
    }

    const protocolIndex = inspection.protocol.findIndex(
      (protocolItem) => protocolItem._id === protocolItemId
    );
    if (protocolIndex === -1) {
      return;
    }

    const protocolItem = inspection.protocol[protocolIndex];
    const answers = [...(protocolItem.answers || [])];
    const answerIndex = answers.findIndex(
      (existingAnswer) => existingAnswer._id === answer._id
    );

    const normalizedAnswer = protocolAnswerInDtoToProtocolAnswer(answer);

    if (answerIndex === -1) {
      answers.push(normalizedAnswer);
    } else {
      answers[answerIndex] = {
        ...answers[answerIndex],
        ...normalizedAnswer,
      };
    }

    const complianceCheck = { ...protocolItem.complianceCheck };
    if (
      normalizedAnswer.type === AnswerType.Compliance &&
      isComplianceProtocolAnswer(normalizedAnswer) &&
      normalizedAnswer.selectedOption !== undefined
    ) {
      complianceCheck.result = normalizedAnswer.selectedOption;
    }

    const updatedProtocol = {
      ...protocolItem,
      answers,
      complianceCheck,
    };

    const updatedProtocolList = [...inspection.protocol];
    updatedProtocolList[protocolIndex] = updatedProtocol;

    await db.inspections.update(inspectionId, {
      protocol: updatedProtocolList,
    });
  }
);

export const patchProtocolAnswersApplicability = wrapQuery(
  db,
  async ({
    inspectionId,
    protocolItemIds,
    isApplicable,
  }: {
    inspectionId: string;
    protocolItemIds: string[];
    isApplicable: boolean;
  }): Promise<void> => {
    if (protocolItemIds.length === 0) {
      return;
    }

    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) {
      return;
    }

    const updatedProtocolList = inspection.protocol.map((protocolItem) => {
      const matchesItem = protocolItemIds.some((protocolItemId) =>
        compareObjectIds(protocolItemId, protocolItem._id)
      );
      if (!matchesItem) {
        return protocolItem;
      }

      return {
        ...protocolItem,
        answers: (protocolItem.answers || []).map((answer) => ({
          ...answer,
          isApplicable,
        })),
      };
    });

    await db.inspections.update(inspectionId, {
      protocol: updatedProtocolList,
    });
  }
);

export const upsertProtocolItemComment = wrapQuery(
  db,
  async ({
    inspectionId,
    protocolItem,
  }: {
    inspectionId: string;
    protocolItem: ProtocolItemInDto;
  }): Promise<void> => {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) {
      return;
    }

    const normalized = normalizeProtocolItemInDto(protocolItem);
    const protocolIndex = inspection.protocol.findIndex(
      (item) => item._id === normalized._id
    );
    if (protocolIndex === -1) {
      return;
    }

    const existing = inspection.protocol[protocolIndex];
    const updatedProtocol = {
      ...existing,
      modifiedAt: normalized.modifiedAt ?? existing.modifiedAt,
      modifiedBy: normalized.modifiedBy ?? existing.modifiedBy,
      complianceCheck: {
        ...existing.complianceCheck,
        comment: normalized.complianceCheck?.comment,
        result:
          normalized.complianceCheck?.result ??
          existing.complianceCheck.result ??
          ComplianceAnswerOption.unset,
        issues:
          normalized.complianceCheck?.issues ?? existing.complianceCheck.issues,
      },
      answers: normalized.answers?.map(protocolAnswerInDtoToProtocolAnswer) ??
        existing.answers,
    };

    const updatedProtocolList = [...inspection.protocol];
    updatedProtocolList[protocolIndex] = updatedProtocol;

    await db.inspections.update(inspectionId, {
      protocol: updatedProtocolList,
      modifiedAt: normalized.modifiedAt ?? inspection.modifiedAt,
      modifiedBy: normalized.modifiedBy ?? inspection.modifiedBy,
    });
  }
);

export const upsertProtocolSectionComment = wrapQuery(
  db,
  async ({
    inspectionId,
    section,
  }: {
    inspectionId: string;
    section: {
      _id: string;
      checklistSectionId?: string;
      comment?: string;
      modifiedAt?: string;
      modifiedBy?: string;
    };
  }): Promise<void> => {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) {
      return;
    }

    const currentSections = inspection.sections || [];
    const updatedSections = [...currentSections];
    const existingSectionIndex = updatedSections.findIndex(
      (inspectionSection) =>
        inspectionSection._id === section._id ||
        (section.checklistSectionId != null &&
          inspectionSection.checklistSectionId ===
            section.checklistSectionId)
    );

    if (existingSectionIndex === -1) {
      updatedSections.push({
        _id: section._id,
        checklistSectionId: section.checklistSectionId,
        comment: section.comment || '',
        modifiedAt: section.modifiedAt,
        modifiedBy: section.modifiedBy,
      });
    } else {
      updatedSections[existingSectionIndex] = {
        ...updatedSections[existingSectionIndex],
        checklistSectionId:
          section.checklistSectionId ||
          updatedSections[existingSectionIndex].checklistSectionId,
        comment: section.comment || '',
        modifiedAt:
          section.modifiedAt || updatedSections[existingSectionIndex].modifiedAt,
        modifiedBy:
          section.modifiedBy || updatedSections[existingSectionIndex].modifiedBy,
      };
    }

    await db.inspections.update(inspectionId, {
      sections: updatedSections,
    });
  }
);

export const removeBatch = wrapQuery(
  db,
  async (ids: string[]): Promise<string[]> => {
    return db
      .transaction('rw', db.inspections, db.documents, async function () {
        const deleteIssues = db.inspections
          .where('_id')
          .anyOf(ids)
          .delete()
          .catch((e) =>
            logWithRethrow({
              logLevel: LogLevel.INFO,
              msg: getErrorMsg(Operation.removeBatch, 'inspections'),
              errorObj: e,
              additionalInfo: { query: { ids } },
            })
          );
        const deleteDocuments = removeDocumentsBatch({
          inspectionId: ids,
        });

        await Promise.all([deleteIssues, deleteDocuments]);
        return ids;
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorTransactionMsg(
            [Operation.removeBatch],
            ['inspections', 'documents']
          ),
          errorObj: e,
          additionalInfo: {
            query: {
              ids,
            },
          },
        })
      );
  }
);
